/**
 * chaseCameraMath - pure third-person camera geometry (no Babylon imports,
 * mirrors src/ui/hotspots/hotspotState.js). ChaseCameraRig feeds it the
 * player camera's pose each frame; it answers where the chase camera wants
 * to sit and which way it faces. The wall raycast that shortens the boom is
 * the rig's job - resolveBoomLength just folds its result in.
 *
 * Conventions match the player UniversalCamera: yaw 0 faces +Z
 * (forward.x = sin yaw, forward.z = cos yaw - the same math as the minimap
 * heading), positive pitch (rotation.x) looks DOWN (forward.y = -sin pitch),
 * so looking down lifts the chase camera above the player's head.
 */

/**
 * @typedef {{x: number, y: number, z: number}} Vec3Like
 */

/**
 * @typedef {Object} ChaseOptions
 * @property {number} boom - full follow distance behind the player's head
 * @property {number} headLift - head anchor height above the eye position
 * @property {number} margin - gap kept between the camera and a hit wall
 * @property {number} minBoom - the boom never collapses below this
 * @property {number} pitchClamp - |pitch| ceiling in radians
 */

/** @type {ChaseOptions} */
export const CHASE_DEFAULTS = {
  boom: 5.5,
  // Above the pod's top (eye +0.4 would stare straight at its back): the
  // camera looks over the avatar's head, so the pod sits low in frame and
  // screen center stays on the world, not on your own balloon.
  headLift: 1.1,
  margin: 0.4,
  minBoom: 0.8,
  pitchClamp: 1.2,
};

/**
 * Where the chase camera wants to be for a player pose.
 * @param {{x: number, y: number, z: number, yaw: number, pitch: number}} pose
 * @param {Partial<ChaseOptions>} [opts]
 * @returns {{head: Vec3Like, forward: Vec3Like, desired: Vec3Like, pitch: number}}
 *   head = boom pivot; forward = unit look direction; desired = camera
 *   position at full boom; pitch = the clamped pitch to render with
 */
export function computeChaseGeometry(pose, opts = {}) {
  const o = { ...CHASE_DEFAULTS, ...opts };
  const pitch = Math.max(-o.pitchClamp, Math.min(o.pitchClamp, pose.pitch || 0));
  const cosPitch = Math.cos(pitch);
  const forward = {
    x: Math.sin(pose.yaw) * cosPitch,
    y: -Math.sin(pitch),
    z: Math.cos(pose.yaw) * cosPitch,
  };
  const head = { x: pose.x, y: pose.y + o.headLift, z: pose.z };
  const desired = {
    x: head.x - forward.x * o.boom,
    y: head.y - forward.y * o.boom,
    z: head.z - forward.z * o.boom,
  };
  return { head, forward, desired, pitch };
}

/**
 * Fold a boom raycast result into the final follow distance.
 * @param {number} boom - the full boom length
 * @param {number|null} hitDistance - distance to the first obstruction along
 *   the head->desired ray, or null when the ray hit nothing
 * @param {{margin?: number, minBoom?: number}} [opts]
 * @returns {number}
 */
export function resolveBoomLength(boom, hitDistance, opts = {}) {
  const margin = opts.margin ?? CHASE_DEFAULTS.margin;
  const minBoom = opts.minBoom ?? CHASE_DEFAULTS.minBoom;
  if (hitDistance == null || !Number.isFinite(hitDistance)) return boom;
  return Math.max(minBoom, Math.min(boom, hitDistance - margin));
}
